/*
 * Copyright (c) 2023, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React from 'react'
import loadable from '@loadable/component'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {useParams} from 'react-router-dom'

// Components
import {Skeleton} from '@salesforce/retail-react-app/app/components/shared/ui'
import {configureRoutes} from '@salesforce/retail-react-app/app/utils/routes-utils'
import {routes as _routes} from '@salesforce/retail-react-app/app/routes'
import AboutUsroutes from './about-us-routes'
import CustomerServiceRoutes from './customer-service-routes'
import BlogDetail from './pages/blog-detail'
import SupportRoutes from './support-routes'
import LeftNavLayout from './components/left-nav-layout'
import LeftNav from './components/left-nav'
import useFetchLibraryFolders from './hooks/useFetchLibrary'
import LoadingSpinner from './components/loading-spinner'

const fallback = <Skeleton height="75vh" width="100%" />

// Create your pages here and add them to the routes array
// Use loadable to split code into smaller js chunks
export const Home = loadable(() => import('./pages/home'), {fallback})
const ProductDetail = loadable(() => import('./pages/product-detail'), {fallback})
const ProductList = loadable(() => import('./pages/product-list'), {fallback})
const ContentDetails = loadable(() => import('./pages/content/content-details'), {fallback})
const StoreLocator = loadable(() => import('./pages/store-locator'), {fallback})
const Cart = loadable(() => import('./pages/cart'), {fallback})
const Storedetails = loadable(() => import('./pages/storedetails'), {fallback})
const Login = loadable(() => import('./pages/login'), {fallback})
const Registration = loadable(() => import('./pages/registration'), {fallback})
const ResetPassword = loadable(() => import('./pages/reset-password'), {fallback})
const ResetPasswordLanding = loadable(() => import('./pages/reset-password-landing'), {fallback})
const Checkout = loadable(() => import('./pages/checkout'), {fallback})
const Account = loadable(() => import('./pages/account'), {fallback})
const GiftCard = loadable(() => import('./pages/gift-card'), {fallback})
const GiftCertPurchase = loadable(() => import('./pages/gift-cert-purchase'), {fallback})
const GuestAccount = loadable(() => import('./pages/guest-account'), {fallback})
const BrandLandingContent = loadable(() => import('./pages/brands-landing'), {fallback})
const BlogListing = loadable(() => import('./pages/blog-listing'), {fallback})
const EmailVerification = loadable(() => import('./pages/email-verification'), {fallback})
const Category = loadable(() => import('./pages/category'), {fallback})

// CheckoutConfirmation page from Adyen
const CheckoutConfirmation = loadable(() => import('./pages/checkout/confirmation'), {
    fallback: fallback
})

// Checkout Error page from Adyen
const AdyenCheckoutError = loadable(() => import('./pages/checkout/error'), {
    fallback: fallback
})

// Checkout Redirect page from Adyen
const AdyenCheckoutRedirect = loadable(() => import('./pages/checkout/redirect'), {
    fallback: fallback
})

// paypal return
const PaypalReturn = loadable(() => import('./pages/checkout/partials/paypal/paypalReturn'), {
    fallback: fallback
})

// paypal cancel
const PaypalCancel = loadable(() => import('./pages/checkout/partials/paypal/paypalCancel'), {
    fallback: fallback
})

const PageNotFound = loadable(() => import('./pages/page-not-found'))

// Create a loadable page for `page-viewer`.
const PageViewer = loadable(() => import('./pages/page-viewer'), {fallback})

const ContentDetailsComponent = () => {
    const {contentID} = useParams()
    const [showLeftNav, setShowLeftNav] = React.useState(false)
    const [isLoading, setIsLoading] = React.useState(true)

    // Fetch customer service folders to check if current content is categorized
    const {data: {folders = []} = {}, loading: foldersLoading} = useFetchLibraryFolders(
        'folders/customer-service'
    )

    React.useEffect(() => {
        if (!foldersLoading) {
            // Check if current contentID exists in any customer service folder
            const isContentCategorized = folders.some((folder) =>
                folder.c_contentAssets?.some((asset) => asset.id === contentID)
            )
            setShowLeftNav(isContentCategorized)
            setIsLoading(false)
        }
    }, [folders, foldersLoading, contentID])

    if (isLoading) {
        return <LoadingSpinner />
    }

    // If content is categorized (exists in customer service folders), show with left nav
    if (showLeftNav) {
        return (
            <LeftNavLayout sidebar={<LeftNav />}>
                <ContentDetails />
            </LeftNavLayout>
        )
    }

    // If content is not categorized, show without left nav
    return <ContentDetails />
}

// Filter out the "*" route from _routes
const filteredRoutes = _routes.filter((route) => route.path !== '*')

const routes = [
    {
        path: '/',
        component: Home,
        exact: true
    },
    ...AboutUsroutes,
    ...CustomerServiceRoutes,
    ...SupportRoutes,
    {
        path: '/content/:contentID.html',
        component: ContentDetailsComponent,
        exact: true
    },
    {
        path: '/blog/:contentID.html',
        component: BlogDetail,
        exact: true
    },
    {
        path: '/blog/:folderId',
        component: BlogListing,
        expect: true
    },
    {
        path: '/shop/:categoryId',
        component: ProductList
    },
    {
        path: '/all-brands',
        component: BrandLandingContent
    },
    {
        path: '/search',
        component: ProductList
    },
    {
        path: '/products/:productName/:productId.html',
        component: ProductDetail
    },
    {
        path: '/storelocator',
        component: StoreLocator
    },
    {
        path: '/storedetails',
        component: Storedetails,
        exact: true
    },
    {
        path: '/register',
        component: Registration
    },
    {
        path: '/login',
        component: Login,
        exact: true
    },
    {
        path: '/reset-password',
        component: ResetPassword,
        exact: true
    },
    {
        path: '/setpassword',
        component: ResetPasswordLanding,
        exact: true
    },
    {
        path: '/cart',
        component: Cart
    },
    {
        path: '/checkout',
        component: Checkout,
        exact: true
    },
    {
        path: '/checkout/error',
        component: AdyenCheckoutError
    },
    {
        path: '/checkout/redirect/handler',
        component: AdyenCheckoutRedirect
    },
    {
        path: '/checkout/confirmation/:orderNo',
        component: CheckoutConfirmation
    },
    {
        path: '/account',
        component: Account
    },
    {
        path: '/cavenders-gift-cards',
        component: GiftCard
    },
    {
        path: '/giftcertpurchase',
        component: GiftCertPurchase
    },
    {
        path: '/checkout/paypal/return',
        component: PaypalReturn
    },
    {
        path: '/checkout/paypal/cancel',
        component: PaypalCancel
    },
    {
        path: '/verify-email',
        component: EmailVerification
    },
    {
        path: '/guest',
        component: GuestAccount
    },
    {
        path: '/category/:categoryId',
        component: Category
    },
    {
        path: '/page-viewer/:pageId',
        component: PageViewer
    },
    ...filteredRoutes,
    {
        path: '*',
        component: PageNotFound
    }
]

export default () => {
    const config = getConfig()
    return configureRoutes(routes, config, {
        ignoredRoutes: ['/callback', '*']
    })
}
